import { useSeo } from "@/lib/seo";
import { useState } from 'react';
import AppHeader from '@/pages/home/components/AppHeader';
import Footer from '@/pages/home/components/Footer';
import AppEnding from '@/pages/home/components/AppEnding';
import Reveal from '@/pages/lot-size/components/Reveal';
import { useTechnicalStudio } from './useTechnicalStudio';
import Studio, { type ToolKey } from './components/Studio';
import ConfluenceScanner from './components/ConfluenceScanner';
import WorkflowSection from './components/WorkflowSection';
import ToolChooser from './components/ToolChooser';
import FormulaSection from './components/FormulaSection';
import TechnicalNotes from './components/TechnicalNotes';
import RelatedTools from './components/RelatedTools';
import BrokerSection from './components/BrokerSection';

export default function TechnicalPage() {
  useSeo({
    /* @replace:technical-title */
    title: 'Phân tích Kỹ thuật Forex | Chỉ báo, Biểu đồ & Tín hiệu Giao dịch - ForexMatrix',
    /* @replace:technical-description */
    description: 'Cập nhật các phương pháp Phân tích Kỹ thuật (Technical Analysis) chuyên sâu cho thị trường ngoại hối. Tổng hợp các chỉ báo kỹ thuật, mô hình giá và công cụ hỗ trợ trader đưa ra quyết định mua bán chính xác.',
    /* @replace:technical-keywords */
    keywords: 'phân tích kỹ thuật forex, technical analysis forex, chỉ báo kỹ thuật ngoại hối, mô hình giá forex, tín hiệu giao dịch, forex matrix',
    path: '/phan-tich-ky-thuat',
  });
  const api = useTechnicalStudio();
  const [activeTool, setActiveTool] = useState<ToolKey>('pivot');

  const scrollToStudio = () => {
    const el = document.getElementById('studio');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleChooserSelect = (anchor: string) => {
    if (anchor === 'pivot' || anchor === 'fibonacci' || anchor === 'strategy') {
      setActiveTool(anchor);
    }
    scrollToStudio();
  };

  return (
    <div className="relative min-h-screen bg-[#F8FAFC] text-slate-900">
      <AppHeader />
      <main className="pb-8">
        <div className="mx-auto max-w-6xl px-4 pt-28 pb-16 md:px-8 md:pt-32 md:pb-20">
          {/* Header */}
          <Reveal>
            <div className="max-w-2xl">
              <span className="inline-flex items-center rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700 ring-1 ring-violet-100">
                Technical Analysis Studio
              </span>
              <h1 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
                Phân tích Kỹ thuật
              </h1>
              <p className="mt-4 text-base leading-relaxed text-slate-600 md:text-lg">
                Xác định các mức giá quan trọng, vẽ vùng hỗ trợ – kháng cự bằng Fibonacci, và đánh giá hiệu quả chiến
                lược giao dịch bằng dữ liệu thực tế.
              </p>
            </div>

            <ul className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-6">
              <li className="rounded-xl border border-slate-200/80 bg-white p-5">
                <div className="h-1 w-10 rounded-full bg-emerald-500" />
                <p className="mt-3 text-sm font-semibold text-slate-900">Máy tính Pivot Point</p>
                <p className="mt-1.5 text-xs leading-relaxed text-slate-500">
                  Xác định các mức hỗ trợ và kháng cự tham chiếu từ dữ liệu giá, giúp bạn biết vùng giá nào quan trọng để vào hoặc thoát lệnh.
                </p>
              </li>
              <li className="rounded-xl border border-slate-200/80 bg-white p-5">
                <div className="h-1 w-10 rounded-full bg-violet-500" />
                <p className="mt-3 text-sm font-semibold text-slate-900">Máy tính Fibonacci</p>
                <p className="mt-1.5 text-xs leading-relaxed text-slate-500">
                  Tìm các vùng giá tiềm năng dựa trên một khoảng cao – thấp, hỗ trợ nhận diện vùng hồi và vùng mở rộng của giá.
                </p>
              </li>
              <li className="rounded-xl border border-slate-200/80 bg-white p-5">
                <div className="h-1 w-10 rounded-full bg-amber-500" />
                <p className="mt-3 text-sm font-semibold text-slate-900">Phân tích dữ liệu chiến lược</p>
                <p className="mt-1.5 text-xs leading-relaxed text-slate-500">
                  Đánh giá kết quả lịch sử và các chỉ số hiệu suất của một chiến lược giao dịch, để biết nó có đáng tin cậy hay không.
                </p>
              </li>
            </ul>
          </Reveal>

          {/* Studio with tabbed tools */}
          <div id="studio" className="mt-12 scroll-mt-24">
            <Reveal delay={60}>
              <Studio api={api} active={activeTool} onSelect={setActiveTool} />
            </Reveal>
          </div>

          {/* Confluence scanner */}
          <div className="mt-16">
            <Reveal>
              <ConfluenceScanner api={api} />
            </Reveal>
          </div>

          {/* Workflow + supporting modules */}
          <div className="mt-16 space-y-12">
            <Reveal>
              <WorkflowSection />
            </Reveal>
            <Reveal>
              <ToolChooser onSelect={handleChooserSelect} />
            </Reveal>
            <Reveal>
              <FormulaSection />
            </Reveal>
            <Reveal>
              <TechnicalNotes />
            </Reveal>
            <Reveal>
              <RelatedTools />
            </Reveal>
            <Reveal>
              <BrokerSection />
            </Reveal>
          </div>
        </div>
      </main>
      <Footer />
      <AppEnding />
    </div>
  );
}